import { Region } from "contentstack";
import type { Swiper as SwiperType } from 'swiper';

export const brandDefaultResourceImages: Record<string, string> = {
  form_technologies: 'https://azure-na-images.contentstack.com/v3/assets/bltc3d459c709c6e7a5/bltcc3e67446b3743c8/Form Background.png',
  dynacast: 'https://azure-na-images.contentstack.com/v3/assets/bltc3d459c709c6e7a5/blt90011881556e5611/Dynacast Background',
  optimim: 'https://azure-na-images.contentstack.com/v3/assets/bltc3d459c709c6e7a5/blt3af9f859e28f2e6f/OptiMIM Background',
  signicast: 'https://azure-na-images.contentstack.com/v3/assets/bltc3d459c709c6e7a5/blta1520c39560a5984/Signicast Background.png',
  techmire: 'https://azure-na-images.contentstack.com/v3/assets/bltc3d459c709c6e7a5/bltcc3e67446b3743c8/66d827cf424115c86edd5418/Pattern.png',
  cirex: 'https://azure-na-images.contentstack.com/v3/assets/bltc3d459c709c6e7a5/bltcc3e67446b3743c8/66d827cf424115c86edd5418/Pattern.png',
};

export const brandPrimary500Color = {
    dynacast: "#2273BA",
    signicast: "#D9272E",
    optimim: "#5B5297",
    techmire: "#2273BA",
    cirex: "#00A19B",
    form_technologies: "#00A19B",
  };

export type BrandName = keyof typeof brandPrimary500Color;

export const currentBrandPrimary500 = brandPrimary500Color[
  (process.env.CONTENTSTACK_BRAND as BrandName) ?? 'dynacast'
];

// API host URL
export const customHostUrl = (baseUrl = ""): string => {
    return baseUrl.replace("api", "cdn");
  };
  
  // Generate prod API URLs based on region
  export const generateUrlBasedOnRegion = (): string[] => {
    return Object.keys(Region).map((region) => {
      if (region === "US") {
        return `cdn.contentstack.io`;
      }
      return `${region}-cdn.contentstack.com`;
    });
  };
  
  // Prod URL validation for custom host
  export const isValidCustomHostUrl = (url: string): boolean => {
    return url ? !generateUrlBasedOnRegion().includes(url) : false;
  };
  
  export const generateVideoThumbnail = (videoUrl: string): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const video = document.createElement("video");
      video.src = videoUrl;
      video.crossOrigin = "anonymous";
  
      video.addEventListener("loadeddata", () => {
        video.currentTime = 1; // Seek to a specific time
      });
  
      video.addEventListener("seeked", () => {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(video, 0, 0);
          resolve(canvas.toDataURL()); // Get the base64 image URL
        } else {
          reject(new Error("Unable to get canvas context"));
        }
      });
  
      video.addEventListener("error", () => {
        reject(new Error("Error loading video"));
      });
    });
  };
  
  export const generateWistiaThumbnail = async (
    wistia: string
  ): Promise<string> => {
    const jsonLink = extractWistiaJsonpUrl(wistia);
    if (!jsonLink) throw Error("Error getting wistia thumbnail");
    const response = await fetch(jsonLink);
    const text = await response.text();
    const jsonpData = text.match(/({.*})/);
  
    if (!jsonpData) throw Error("Error getting wistia thumbnail");
  
    const json = JSON.parse(jsonpData?.[0]);
    const asset =
      json?.media?.assets?.find((a: any) => a?.type === "original") ||
      json?.media?.assets?.[0];
      
    // preview middle of video
    const previewTime = Math.round((json?.media?.duration || 0) / 2)
    if (asset?.url) {
      const jpgUrl = asset.url.replace(".bin", ".jpg") + '?video_still_time=' + previewTime;
      return jpgUrl;
    }
    throw Error("Error getting wistia thumbnail");
  };
  
  export const getLocalizedUrl = (currentLocale: string, path: string) => {
    // Ensure path starts with "/"
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `/${currentLocale}${normalizedPath}`;
  };
  
  export const extractWistiaIdFromHtmlSnippet = (htmlSnippet: string) => {
    const mediaMatch = htmlSnippet.match(/\/embed\/medias\/([a-zA-Z0-9]+)/);
    const iframeMatch = htmlSnippet.match(/\/embed\/iframe\/([a-zA-Z0-9]+)/);
  
    if (mediaMatch) {
      return mediaMatch[1];
    } else if (iframeMatch) {
      return iframeMatch[1];
    }
  
    return null;
  };
  
  export const extractWistiaJsonpUrl = (inputString: string) => {
    const scriptMatch = inputString.match(
      /https:\/\/fast\.wistia\.com\/embed\/medias\/\w+\.jsonp/
    );
    return scriptMatch ? scriptMatch[0] : null;
  };
  
  export const handleSwiperBreakpoint = (
    swiper: SwiperType,
    setSlidesPerView: React.Dispatch<React.SetStateAction<number>>
  ) => {
    if (swiper.params.slidesPerView === 'auto') {
      setSlidesPerView(swiper.slidesPerViewDynamic())
      return
    }
    setSlidesPerView(swiper.params.slidesPerView as number);
  };
  
  export const isValidLocale = (url: string) => {
    const pathSegments = url.split("/").filter(Boolean);
    return pathSegments[0];
  }
  
  export const setLocalStorageItem = (key: string, value: unknown) => {
    try {
        const serializedValue = JSON.stringify(value);
        localStorage.setItem(key, serializedValue);
    } catch (error) {
        console.error("Error setting local storage item:", error);
    }
  };
  
  export const getLocalStorageItem = (key: string) => {
    try {
        const serializedValue = localStorage.getItem(key);
        return serializedValue ? JSON.parse(serializedValue) : null;
    } catch (error) {
        console.error("Error getting local storage item:", error);
        return null;
    }
  };
  
  export const removeLocalStorageItem = (key: string) => {
    try {
        localStorage.removeItem(key);
    } catch (error) {
        console.error("Error removing local storage item:", error);
    }
  };
  
  export const scrollToTopSmooth = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }